import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import {
  Search,
  MoreHorizontal,
  Settings,
  LogOut,
  Shield,
  X,
  Bot,
  Loader2,
  AlertTriangle,
  Flame,
  Tag,
  Bell,
  Lock,
  MapPin,
  ChevronRight,
  User,
  Gift,
  Sun,
  Moon,
    MessageCircle,
    Package,
    ShoppingBasket,
    Users,
} from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { useCommunityWinsUnseen } from "../hooks/useCommunityWinsUnseen";
import { useTranslation } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { logOut } from "../services/AuthService";
import { isFreeTier, isUserSetUp } from "../utils/userUtils";
import {
  linkDiscord,
  completeDiscordLink,
} from "../services/DiscordService";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useProductContext } from "../context/ProductContext";
import ZipCodeModal from "./ZipCodeModal";
import { track } from "../utils/analytics";
import PennyMealsPromo from "./PennyMealsPromo";
import SidebarRetailSelector from "./SidebarRetailSelector";
import { isBestDealsPath } from "../utils/bestDealsNavigation";

type BottomNavItem = {
  path: string;
  label: string;
  icon: typeof Flame;
  tourId?: string;
  locked?: boolean;
};

export default function BottomNavigation() {
  const location = useLocation();
  const { user, load } = useUserContext();
  const { hasNew: hasNewCommunityWins } = useCommunityWinsUnseen();
  const { t, currentLanguage, setLanguage } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const queryClient = useQueryClient();
  const { selectedProduct } = useProductContext();
  const isFreeUser = isFreeTier(user);
  const isBestDealsPage = isBestDealsPath(location.pathname);
  const selectedStoreId = new URLSearchParams(location.search).get("storeId");
  const isOnlineStoreMode = isFreeUser || selectedStoreId === "online";
  // The products tab is the user's own store feed; online-only users have no
  // stores pinned, so it stays "Deals" for them.
  const productsTabLabel = isOnlineStoreMode ? "Deals" : "My stores";
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [sheetVisible, setSheetVisible] = useState(false);

  // Discord modal state
  const [showDiscordModal, setShowDiscordModal] = useState(false);
  const [isLinkingDiscord, setIsLinkingDiscord] = useState(false);

  // Zip code modal state
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);

  // Handle Discord linking completion from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const discordLinkSuccess = urlParams.get("discord_link_success");
    const discordUserId = urlParams.get("discord_user_id");
    const discordRefreshToken = urlParams.get("discord_refresh_token");

    if (discordLinkSuccess === "true" && discordUserId && discordRefreshToken) {
      handleCompleteDiscordLink(discordUserId, discordRefreshToken);
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleLogout = () => {
    queryClient.clear();
    logOut();
  };

  const handleLinkDiscord = async () => {
    if (!user) return;

    setIsLinkingDiscord(true);
    try {
      const response = await linkDiscord();
      // Redirect to Discord OAuth
      window.location.href = response.redirectUrl;
    } catch (error) {
      console.error("Error linking Discord:", error);
      toast.error(t("settings.discordLinkError"));
      setIsLinkingDiscord(false);
    }
  };

  const handleCompleteDiscordLink = async (
    discordUserId: string,
    discordRefreshToken: string
  ) => {
    try {
      await completeDiscordLink({ discordUserId, discordRefreshToken });
      await load(); // Reload user data
      toast.success(t("settings.discordLinkedSuccess"));
      setShowDiscordModal(false);
      // Refresh the page to update the navigation
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error completing Discord link:", error);
      toast.error(t("settings.discordLinkError"));
    }
  };

  const handleZipCodeSuccess = () => {
    setShowZipCodeModal(false);
    setShowMoreModal(false);
  };

  const openMoreModal = () => {
    setShowMoreModal(true);
  };

  // Trigger slide-in animation on next frame after mount
  useEffect(() => {
    if (showMoreModal) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setSheetVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setSheetVisible(false);
  }, [showMoreModal]);

  const closeMoreModal = () => {
    setSheetVisible(false);
    setTimeout(() => {
      setShowMoreModal(false);
    }, 300);
  };

  // Discord SVG Icon Component
  const DiscordIcon = ({
    size = 20,
    className = "",
  }: {
    size?: number;
    className?: string;
  }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z" />
    </svg>
  );

  // Main navigation items for bottom bar
  const mainNavItems = useMemo(() => {
    const items: BottomNavItem[] = [
      {
        path: isOnlineStoreMode ? `/${selectedProduct}/products?storeId=online` : `/${selectedProduct}/products`,
        label: productsTabLabel,
        icon: Flame,
        tourId: "products",
        locked: false,
      },
    ];

    if (isFreeUser) {
      return [
        ...items,
        { path: "/onboarding?intent=local&feature=search", label: "Search", icon: Search, tourId: "search", locked: true },
        { path: "/onboarding?intent=local&feature=leads", label: "Leads", icon: Tag, tourId: "leads", locked: true },
        { path: "/onboarding?intent=local&feature=alerts", label: "Alerts", icon: Bell, tourId: "alerts", locked: true },
      ];
    }

    // For Woot and online-store mode, only show Store and More.
    if (selectedProduct === "woot" || selectedProduct === "kohls" || isOnlineStoreMode) {
      return items;
    }

    // For Lowe's, show Store and Leads only (nearby is on product cards).
    if (selectedProduct === "lowes") {
      return [
        { path: `/${selectedProduct}/products`, label: t("nav.store"), icon: Flame, tourId: "products" },
        { path: `/${selectedProduct}/leads`, label: "Leads", icon: Tag, tourId: "leads" },
      ];
    }

    // For Best Buy and Costco, show Store, Leads, and Search.
    if (selectedProduct === "bestbuy" || selectedProduct === "costco") {
      items.push(
        { path: `/${selectedProduct}/product-search`, label: t("nav.productSearch"), icon: Search, tourId: "search" },
        { path: `/${selectedProduct}/leads`, label: "Leads", icon: Tag, tourId: "leads" }
      );
      return items;
    }

    // For Target, show Store, Leads, and Search.
    if (selectedProduct === "target") {
      items.push(
        { path: `/${selectedProduct}/product-search`, label: t("nav.productSearch"), icon: Search, tourId: "search" },
        { path: `/${selectedProduct}/leads`, label: "Leads", icon: Tag, tourId: "leads" }
      );
      return items;
    }

    // Dollar General's mobile flow prioritizes products, coupon baskets,
    // search, and alerts. Leads remains available in the desktop sidebar.
    if (selectedProduct === "dollar_general") {
      items.push(
        { path: `/${selectedProduct}/coupons`, label: "Combos", icon: ShoppingBasket, tourId: "coupon-combos" },
        { path: `/${selectedProduct}/product-search`, label: t("nav.productSearch"), icon: Search, tourId: "search" }
      );
      if (!isFreeUser) {
        items.push({ path: `/${selectedProduct}/tracking`, label: "Alerts", icon: Bell, tourId: "alerts" });
      }
      return items;
    }

    // For other retailers (walmart, home_depot), show all items
    items.push(
      { path: `/${selectedProduct}/product-search`, label: t("nav.productSearch"), icon: Search, tourId: "search" },
      { path: `/${selectedProduct}/leads`, label: "Leads", icon: Tag, tourId: "leads" }
    );
    if (!isFreeUser) {
      items.push({ path: `/${selectedProduct}/tracking`, label: "Alerts", icon: Bell, tourId: "alerts" });
    }

    return items;
  }, [selectedProduct, t, isOnlineStoreMode, isFreeUser, productsTabLabel]);

  // More menu items (Saved/Favorites is now handled via ProductsPage filter)
  const moreItems = useMemo(() => {
    const items: { path: string; label: string; icon: typeof Users; badge?: boolean }[] = [];

    // For Woot, online-store mode, and bestbuy, no bot-filters needed
    // For other retailers, add bot-filters
    if (selectedProduct !== "woot" && selectedProduct !== "kohls" && !isOnlineStoreMode && selectedProduct !== "bestbuy" && selectedProduct !== "lowes") {
      items.push({
        path: `/${selectedProduct}/bot-filters`,
        label: "Change Stores",
        icon: Bot,
      });
    }

    // Settings is always in More menu
    items.push({
      path: "/community-successes",
      label: "Community wins",
      icon: Users,
      badge: hasNewCommunityWins,
    });

    items.push({
      path: "/settings",
      label: t("common.settings"),
      icon: Settings,
    });

    items.push({
      path: "/contact-us",
      label: t("emptyStates.help.menuLabel"),
      icon: MessageCircle,
    });

    // Admin link
    if (user?.admin) {
      items.push({
        path: "/admin/users",
        label: t("common.admin"),
        icon: Shield,
      });
    }

    return items;
  }, [selectedProduct, t, user?.admin, isOnlineStoreMode, hasNewCommunityWins]);

  const isActiveTab = (path: string) => {
    const [pathOnly, query = ""] = path.split("?");
    const currentPath = location.pathname;
    const currentQuery = location.search.startsWith("?")
      ? location.search.slice(1)
      : location.search;
    if (path === "/") {
      return currentPath === "/";
    }
    // For retailer-based paths, check if the current path matches the retailer path
    if (path.includes(`/${selectedProduct}/`)) {
      if (query) {
        return currentPath === pathOnly && currentQuery === query;
      }
      return currentPath === pathOnly;
    }
    return currentPath.startsWith(pathOnly);
  };

  const isMoreActive = () => {
    return moreItems.some((item) => isActiveTab(item.path));
  };

  useEffect(() => {
    document.documentElement.classList.add("has-bottom-navigation");
    return () => {
      document.documentElement.classList.remove("has-bottom-navigation");
    };
  }, []);

  if (!user || (!isUserSetUp(user) && !isFreeTier(user))) {
    return null;
  }

  const bottomNavItemClass =
    "relative z-20 flex flex-col items-center justify-center gap-0.5 px-1 text-white transition-colors duration-200 touch-manipulation";
  const visibleMainNavItems = isBestDealsPage
    ? [
        {
          path: isOnlineStoreMode
            ? `/${selectedProduct}/products?storeId=online`
            : `/${selectedProduct}/products`,
          label: productsTabLabel,
          icon: Package,
          tourId: "products",
        },
      ]
    : mainNavItems;

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-[9999] pointer-events-auto touch-manipulation bottom-navigation-surface"
        style={{
          position: "fixed",
          zIndex: 9999,
          pointerEvents: "auto",
          touchAction: "manipulation",
        }}
      >
          <div
            className="grid relative border-t border-primary-800/50 dark:border-dark-700/50 bottom-navigation-surface backdrop-blur-xl"
            style={{
              gridTemplateColumns: `repeat(${visibleMainNavItems.length + (isBestDealsPage ? 2 : 1)}, minmax(0, 1fr))`,
              gridTemplateRows: isBestDealsPage ? "3.25rem" : "3.5rem",
              paddingBottom: "max(8px, env(safe-area-inset-bottom, 0px))",
            }}
          >
          {/* Main navigation items */}
          {visibleMainNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActiveTab(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-tour={item.tourId}
                aria-label={item.label}
                title={item.label}
                className={bottomNavItemClass}
                onClick={(e) => {
                  e.stopPropagation();
                  track("navigation-clicked", {
                    tab: item.path,
                    tabLabel: item.label,
                    navigationType: "bottom-nav-main",
                    fromPath: location.pathname,
                    selectedProduct,
                  });
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
              >
                <Icon
                  size={isBestDealsPage ? 22 : 22}
                  fill={isActive && Icon !== Search ? "currentColor" : "none"}
                  strokeWidth={isActive ? 2.5 : 2}
                />
                {item.locked && (
                  <span className="absolute left-1/2 top-1 ml-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-300 text-violet-950">
                    <Lock size={8} strokeWidth={3} />
                  </span>
                )}
                <span className="text-[9px] font-bold leading-none">{item.label}</span>
              </Link>
            );
          })}

          {isBestDealsPage && (
            <SidebarRetailSelector
              bottomNavTrigger
              className="relative z-20 h-full"
            />
          )}

          {/* More button */}
          <button
            aria-label={t("common.more")}
            title={t("common.more")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Track navigation click in Mixpanel
              track("navigation-clicked", {
                tab: "more",
                tabLabel: t("common.more"),
                navigationType: "bottom-nav",
                fromPath: location.pathname,
                selectedProduct,
              });
              openMoreModal();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className={bottomNavItemClass}
          >
            <MoreHorizontal
              size={isBestDealsPage ? 22 : 22}
              strokeWidth={isMoreActive() ? 2.75 : 2}
            />
            <span className="text-[9px] font-bold leading-none">More</span>
          </button>
        </div>
      </nav>

      {/* More Modal */}
      {showMoreModal && (
        <div
          className={`md:hidden fixed inset-0 z-[9999] flex items-end transition-colors duration-300 ease-out pointer-events-auto ${
            sheetVisible ? "bg-black/50" : "bg-black/0"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            closeMoreModal();
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t("common.more")}
            className={`relative w-full max-h-[82vh] overflow-y-auto rounded-t-3xl bg-white pt-2 pb-8 text-gray-900 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] transition-transform duration-300 ease-out dark:bg-dark-900 dark:text-white ${
              sheetVisible ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="mx-auto mb-2.5 h-[5px] w-10 rounded-full bg-gray-300 dark:bg-dark-600" />

            {/* Header: title, appearance + language, close */}
            <div className="flex items-center justify-between gap-3 px-4 pb-2.5">
              <h2 className="text-xl font-extrabold tracking-tight">{t("common.more")}</h2>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5 rounded-[9px] bg-gray-100 p-0.5 dark:bg-white/[0.06]" role="radiogroup" aria-label="Appearance">
                  {(["light", "dark"] as const).map((mode) => {
                    const active = (theme === "dark") === (mode === "dark");
                    return (
                      <button
                        key={mode}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (active) return;
                          toggleTheme();
                          track("theme-toggled", { newTheme: mode });
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        className={`flex h-[26px] items-center gap-1 rounded-[7px] px-2 text-xs font-bold transition ${
                          active ? "bg-white text-primary-700 shadow-sm dark:bg-[#3a3532] dark:text-primary-300" : "text-gray-500 dark:text-dark-300"
                        }`}
                      >
                        {mode === "dark" ? <Moon size={13} /> : <Sun size={13} />}
                        {mode === "dark" ? "Dark" : "Light"}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = currentLanguage === "en" ? "es" : "en";
                    setLanguage(next);
                    track("language-toggled", { from: currentLanguage, to: next, source: "bottom-nav" });
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="flex h-8 items-center gap-1 rounded-[9px] bg-gray-100 px-2 text-xs font-bold text-gray-700 dark:bg-white/[0.06] dark:text-dark-200"
                  aria-label={currentLanguage === "en" ? "Switch to Spanish" : "Switch to English"}
                >
                  <span className="text-sm leading-none">{currentLanguage === "en" ? "🇺🇸" : "🇪🇸"}</span>
                  {currentLanguage === "en" ? "EN" : "ES"}
                </button>
                <button
                  type="button"
                  onPointerDown={closeMoreModal}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-900 transition hover:bg-gray-200 dark:bg-white/[0.06] dark:text-white"
                  aria-label={t("common.close")}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="px-4">
              {(() => {
                type MoreRow = { key: string; label: string; icon: React.ReactNode; sub?: string; badge?: boolean; tint?: string; to?: string; onClick?: () => void; danger?: boolean };
                const go = (path: string, label: string) => {
                  track("navigation-clicked", { tab: path, tabLabel: label, navigationType: "bottom-nav-more-menu", fromPath: location.pathname, selectedProduct });
                  closeMoreModal();
                };
                const showChangeStores = selectedProduct !== "woot" && selectedProduct !== "kohls" && !isOnlineStoreMode && selectedProduct !== "bestbuy" && selectedProduct !== "lowes";
                const groups: { label: string; rows: MoreRow[] }[] = [
                  {
                    label: "You",
                    rows: [
                      { key: "profile", label: "Profile", icon: <User size={19} />, sub: user.userId, to: "/settings?tab=profile" },
                      {
                        key: "zip",
                        label: "Zip code",
                        icon: <MapPin size={19} />,
                        sub: user.zipCode || "Not set",
                        onClick: () => {
                          track("navigation-clicked", { tab: "zipcode-edit", tabLabel: "Zip Code", navigationType: "bottom-nav", currentZipCode: user.zipCode || null, fromPath: location.pathname });
                          closeMoreModal();
                          setShowZipCodeModal(true);
                        },
                      },
                    ],
                  },
                  {
                    label: "App",
                    rows: [
                      ...(showChangeStores ? [{ key: "stores", label: "Change Stores", icon: <Bot size={19} />, sub: "Pick the stores you track", to: `/${selectedProduct}/bot-filters` }] : []),
                      { key: "settings", label: t("common.settings"), icon: <Settings size={19} />, sub: "Notifications, retailers, billing", to: "/settings" },
                      { key: "community", label: "Community wins", icon: <Users size={19} />, sub: hasNewCommunityWins ? "New since you looked" : undefined, badge: hasNewCommunityWins, to: "/community-successes" },
                      { key: "help", label: t("emptyStates.help.menuLabel"), icon: <MessageCircle size={19} />, sub: t("emptyStates.help.chat"), to: "/contact-us" },
                      ...(user.admin ? [{ key: "admin", label: t("common.admin"), icon: <Shield size={19} />, to: "/admin/users" }] : []),
                    ],
                  },
                ];
                const rowClass = "flex min-h-[54px] w-full items-center gap-3 text-left";
                const wellClass = "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100 text-primary-700 dark:bg-white/[0.06] dark:text-primary-300";
                const renderRow = (row: MoreRow, last: boolean) => {
                  const inner = (
                    <>
                      <span className={wellClass} style={row.tint ? { color: row.tint } : undefined}>{row.icon}</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className={`text-[15px] font-semibold ${row.danger ? "text-red-600 dark:text-red-400" : ""}`}>{row.label}</span>
                          {row.badge && <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />}
                        </span>
                        {row.sub && <span className="block truncate text-xs text-gray-500 dark:text-dark-300">{row.sub}</span>}
                      </span>
                      <ChevronRight size={16} className="flex-shrink-0 text-gray-400" />
                    </>
                  );
                  const border = last ? "" : " border-b border-gray-100 dark:border-dark-700";
                  if (row.to) {
                    const to = row.to;
                    return (
                      <Link key={row.key} to={to} className={rowClass + border} onClick={(e) => { e.stopPropagation(); go(to, row.label); }} onPointerDown={(e) => e.stopPropagation()}>
                        {inner}
                      </Link>
                    );
                  }
                  return (
                    <button key={row.key} type="button" className={rowClass + border} onClick={(e) => { e.stopPropagation(); row.onClick?.(); }} onPointerDown={(e) => e.stopPropagation()}>
                      {inner}
                    </button>
                  );
                };
                return (
                  <>
                    {groups.map((group) => (
                      <div key={group.label}>
                        <div className="mb-0.5 mt-3 text-[11px] font-bold uppercase tracking-[0.6px] text-gray-500 dark:text-dark-300">{group.label}</div>
                        {group.rows.map((row, index) => renderRow(row, index === group.rows.length - 1))}
                      </div>
                    ))}

                    <Link
                      to="/settings?tab=referrals"
                      onClick={(e) => { e.stopPropagation(); go("/settings?tab=referrals", "Invite & earn 50% off"); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      className="mb-1 mt-4 flex items-center gap-3 rounded-[14px] border border-[#c4b5fd] bg-[#f3e8ff] px-3 py-3 dark:border-violet-400/30 dark:bg-purple-600/15"
                      aria-label="Invite friends and earn 50% off"
                    >
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white"><Gift size={20} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[14.5px] font-extrabold">Invite &amp; earn 50% off</span>
                        <span className="mt-0.5 block text-xs leading-4 text-gray-600 dark:text-dark-200">Give a friend 50% off and bank 50% off a renewal.</span>
                      </span>
                      <ChevronRight size={16} className="flex-shrink-0 text-primary-700 dark:text-purple-300" />
                    </Link>

                    <PennyMealsPromo variant="sheet" isMember={!isFreeUser} fromPath={location.pathname} />

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        track("navigation-clicked", { tab: "logout", tabLabel: t("common.logout"), navigationType: "bottom-nav-more-menu", fromPath: location.pathname, selectedProduct });
                        handleLogout();
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                    >
                      <LogOut size={17} />
                      {t("common.logout")}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Discord Link Modal */}
      {showDiscordModal && (
        <div className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <DiscordIcon size={24} className="text-primary-600 dark:text-primary-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t("settings.linkDiscord")}
                </h2>
              </div>
              <button
                onPointerDown={() => setShowDiscordModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                    {t("settings.discordNotLinked")}
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    {t("settings.discordNotLinkedDescription")}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onPointerDown={() => setShowDiscordModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onPointerDown={handleLinkDiscord}
                  disabled={isLinkingDiscord}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                    isLinkingDiscord
                      ? "bg-gray-100 dark:bg-dark-700 text-gray-400 dark:text-dark-500 cursor-not-allowed"
                      : "bg-primary-600 text-white hover:bg-primary-700 cursor-pointer"
                  }`}
                >
                  {isLinkingDiscord ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4" />
                      {t("settings.linking")}
                    </>
                  ) : (
                    <>
                      <DiscordIcon size={16} />
                      {t("settings.linkDiscord")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zip Code Modal */}
      <ZipCodeModal
        isOpen={showZipCodeModal}
        onClose={() => setShowZipCodeModal(false)}
        onSuccess={handleZipCodeSuccess}
        currentZipCode={user?.zipCode || ""}
        title="Set Zip Code"
        description="Enter your zip code to search nearby stores."
      />
    </>
  );
}
